/**
 * Arc - Circular arc segment
 *
 * Can be defined by:
 * - Center, radius, start/end angles
 * - 3 points (start, through, end)
 *
 * Rhino-style: Arcs can reference Point objects via pointIds.
 * - For 3-point arcs: pointIds = [startPointId, throughPointId, endPointId]
 * - For center-based arcs: centerPointId + optional start/end pointIds
 * When the referenced points move, the arc updates automatically.
 */

import { GeometryType } from '../base/GeometryBase';
import { Vector } from '../base/Vector';
import { CurveBase } from './CurveBase';

export class Arc extends CurveBase {
  /**
   * Create an arc from center and angles
   * @param {Vector|{x,y,z}} center - Center point (or null if using 3-point definition)
   * @param {number} radius - Radius in mm
   * @param {number} startAngle - Start angle in radians
   * @param {number} endAngle - End angle in radians
   * @param {Object} attributes - Including optional pointIds for 3-point definition
   */
  constructor(center, radius, startAngle = 0, endAngle = Math.PI, attributes = {}) {
    super(GeometryType.ARC, 'arc', attributes);

    // Extract point IDs from attributes
    // For 3-point arcs: [startPointId, throughPointId, endPointId]
    this._pointIds = attributes.pointIds || null;
    this._centerPointId = attributes.centerPointId || null;

    if (center) {
      this.center = center instanceof Vector ? center : Vector.fromObject(center);
    } else {
      this.center = new Vector(0, 0, 0);
    }
    this.radius = radius || 0;
    this.startAngle = startAngle;
    this.endAngle = endAngle;
    this.plane = 'XY'; // For now, arcs are in XY plane
  }

  /**
   * Get point IDs for 3-point arc definition
   */
  get pointIds() {
    return this._pointIds;
  }

  /**
   * Set point IDs
   */
  set pointIds(ids) {
    this._pointIds = ids;
    this.touch();
  }

  /**
   * Get center point ID (for center-based arcs)
   */
  get centerPointId() {
    return this._centerPointId;
  }

  /**
   * Set center point ID
   */
  set centerPointId(id) {
    this._centerPointId = id;
    this.touch();
  }

  /**
   * Check if this arc uses point references
   */
  get usesPointReferences() {
    return (this._pointIds && this._pointIds.length === 3) || !!this._centerPointId;
  }

  /**
   * Check if this arc uses 3-point definition
   */
  get uses3PointDefinition() {
    return this._pointIds && this._pointIds.length === 3;
  }

  /**
   * Update internal values from external point state
   * @param {Function} getPoint - Function to get point by ID (returns {x, y, z})
   */
  updateFromPoints(getPoint) {
    if (!this.usesPointReferences) return;

    // 3-point definition
    if (this.uses3PointDefinition) {
      const p1 = getPoint(this._pointIds[0]);
      const p2 = getPoint(this._pointIds[1]);
      const p3 = getPoint(this._pointIds[2]);

      if (p1 && p2 && p3) {
        const start = new Vector(p1.x, p1.y, p1.z || 0);
        const through = new Vector(p2.x, p2.y, p2.z || 0);
        const end = new Vector(p3.x, p3.y, p3.z || 0);

        // Recalculate arc from 3 points
        this._recalculateFrom3Points(start, through, end);
      }
    } else if (this._centerPointId) {
      // Center-based definition
      const centerPt = getPoint(this._centerPointId);
      if (centerPt) {
        this.center = new Vector(centerPt.x, centerPt.y, centerPt.z || 0);
      }
    }
  }

  /**
   * Internal: Recalculate arc parameters from 3 points
   */
  _recalculateFrom3Points(start, through, end) {
    const ax = start.x, ay = start.y;
    const bx = through.x, by = through.y;
    const cx = end.x, cy = end.y;

    const d = 2 * (ax * (by - cy) + bx * (cy - ay) + cx * (ay - by));
    if (Math.abs(d) < 1e-10) {
      // Points are collinear - can't form arc
      return;
    }

    const ux = ((ax * ax + ay * ay) * (by - cy) + (bx * bx + by * by) * (cy - ay) + (cx * cx + cy * cy) * (ay - by)) / d;
    const uy = ((ax * ax + ay * ay) * (cx - bx) + (bx * bx + by * by) * (ax - cx) + (cx * cx + cy * cy) * (bx - ax)) / d;

    this.center = new Vector(ux, uy, start.z);
    this.radius = this.center.distanceTo(start);

    // Calculate angles
    this.startAngle = Math.atan2(start.y - this.center.y, start.x - this.center.x);
    const throughAngle = Math.atan2(through.y - this.center.y, through.x - this.center.x);
    let endAngle = Math.atan2(end.y - this.center.y, end.x - this.center.x);

    // Determine arc direction
    const normalizeAngle = (a, base) => {
      while (a < base) a += 2 * Math.PI;
      while (a > base + 2 * Math.PI) a -= 2 * Math.PI;
      return a;
    };

    const throughNorm = normalizeAngle(throughAngle, this.startAngle);
    const endNorm = normalizeAngle(endAngle, this.startAngle);

    if (throughNorm > endNorm) {
      endAngle = this.startAngle + (2 * Math.PI - (this.startAngle - endAngle + 2 * Math.PI) % (2 * Math.PI));
      if (endAngle < this.startAngle) endAngle += 2 * Math.PI;
    }

    this.endAngle = endAngle;

    // Store points for reference
    this._startPoint = start;
    this._throughPoint = through;
    this._endPoint = end;
  }

  /**
   * Get center point (for consistent API with other geometry)
   */
  get centerPoint() {
    return this.center;
  }

  /**
   * Create arc from 3 points
   * @param {Vector|{x,y,z}} p1 - Start point
   * @param {Vector|{x,y,z}} p2 - Point on arc
   * @param {Vector|{x,y,z}} p3 - End point
   * @param {Object} attributes
   * @returns {Arc|null} Arc or null if points are collinear
   */
  static from3Points(p1, p2, p3, attributes = {}) {
    const start = p1 instanceof Vector ? p1 : Vector.fromObject(p1);
    const through = p2 instanceof Vector ? p2 : Vector.fromObject(p2);
    const end = p3 instanceof Vector ? p3 : Vector.fromObject(p3);

    // Calculate circle center from 3 points
    const ax = start.x, ay = start.y;
    const bx = through.x, by = through.y;
    const cx = end.x, cy = end.y;

    const d = 2 * (ax * (by - cy) + bx * (cy - ay) + cx * (ay - by));
    if (Math.abs(d) < 1e-10) {
      return null; // Points are collinear
    }

    const ux = ((ax * ax + ay * ay) * (by - cy) + (bx * bx + by * by) * (cy - ay) + (cx * cx + cy * cy) * (ay - by)) / d;
    const uy = ((ax * ax + ay * ay) * (cx - bx) + (bx * bx + by * by) * (ax - cx) + (cx * cx + cy * cy) * (bx - ax)) / d;

    const center = new Vector(ux, uy, start.z);
    const radius = center.distanceTo(start);

    // Calculate angles
    const startAngle = Math.atan2(start.y - center.y, start.x - center.x);
    const throughAngle = Math.atan2(through.y - center.y, through.x - center.x);
    let endAngle = Math.atan2(end.y - center.y, end.x - center.x);

    // Determine arc direction (ensure through point is between start and end)
    const normalizeAngle = (a, base) => {
      while (a < base) a += 2 * Math.PI;
      while (a > base + 2 * Math.PI) a -= 2 * Math.PI;
      return a;
    };

    const throughNorm = normalizeAngle(throughAngle, startAngle);
    const endNorm = normalizeAngle(endAngle, startAngle);

    // If through point is not between start and end, we need to go the other way
    if (throughNorm > endNorm) {
      // Go counterclockwise (add 2π to end angle)
      endAngle = startAngle + (2 * Math.PI - (startAngle - endAngle + 2 * Math.PI) % (2 * Math.PI));
      if (endAngle < startAngle) endAngle += 2 * Math.PI;
    }

    const arc = new Arc(center, radius, startAngle, endAngle, attributes);
    arc._startPoint = start;
    arc._throughPoint = through;
    arc._endPoint = end;
    return arc;
  }

  get sweepAngle() {
    let sweep = this.endAngle - this.startAngle;
    // Normalize to -2π to 2π
    while (sweep > 2 * Math.PI) sweep -= 2 * Math.PI;
    while (sweep < -2 * Math.PI) sweep += 2 * Math.PI;
    return sweep;
  }

  get length() {
    return Math.abs(this.sweepAngle) * this.radius;
  }

  get isClosed() {
    return Math.abs(Math.abs(this.sweepAngle) - 2 * Math.PI) < 1e-10;
  }

  pointAt(t) {
    const angle = this.startAngle + t * this.sweepAngle;
    return new Vector(
      this.center.x + this.radius * Math.cos(angle),
      this.center.y + this.radius * Math.sin(angle),
      this.center.z
    );
  }

  tangentAt(t) {
    const angle = this.startAngle + t * this.sweepAngle;
    const sign = this.sweepAngle >= 0 ? 1 : -1;
    return new Vector(
      -sign * Math.sin(angle),
      sign * Math.cos(angle),
      0
    ).normalize();
  }

  get startPoint() {
    return this._startPoint || this.pointAt(0);
  }

  get endPoint() {
    return this._endPoint || this.pointAt(1);
  }

  get midPoint() {
    return this._throughPoint || this.pointAt(0.5);
  }

  /**
   * Offset arc (concentric arc)
   * @param {number} distance - Offset distance (positive = outward)
   * @returns {Arc}
   */
  offset(distance) {
    const newRadius = this.radius + distance;
    if (newRadius <= 0) return null;
    return new Arc(
      this.center.clone(),
      newRadius,
      this.startAngle,
      this.endAngle,
      { ...this.attributes }
    );
  }

  /**
   * Reverse arc direction
   * @returns {Arc}
   */
  reverse() {
    return new Arc(
      this.center.clone(),
      this.radius,
      this.endAngle,
      this.startAngle,
      { ...this.attributes }
    );
  }

  clone() {
    const arc = new Arc(
      this.center.clone(),
      this.radius,
      this.startAngle,
      this.endAngle,
      { ...this.attributes }
    );
    arc._pointIds = this._pointIds ? [...this._pointIds] : null;
    arc._centerPointId = this._centerPointId;
    if (this._startPoint) arc._startPoint = this._startPoint.clone();
    if (this._throughPoint) arc._throughPoint = this._throughPoint.clone();
    if (this._endPoint) arc._endPoint = this._endPoint.clone();
    return arc;
  }

  transform({ translate, rotate, scale, mirror }) {
    let center = this.center.clone();
    let radius = this.radius;
    let startAngle = this.startAngle;
    let endAngle = this.endAngle;

    if (scale) {
      const s = typeof scale === 'number' ? scale : 1;
      center = center.multiply(s);
      radius *= s;
    }

    if (rotate) {
      center = center.rotate2D(rotate);
      startAngle += rotate;
      endAngle += rotate;
    }

    if (translate) {
      center = center.add(Vector.fromObject(translate));
    }

    if (mirror) {
      // Mirror around X axis flips Y and reverses angles
      const n = Vector.fromObject(mirror).normalize();
      center = center.mirror(n);
      // Mirror angles
      if (Math.abs(n.y) > Math.abs(n.x)) {
        // Horizontal mirror axis
        startAngle = -startAngle;
        endAngle = -endAngle;
      } else {
        // Vertical mirror axis
        startAngle = Math.PI - startAngle;
        endAngle = Math.PI - endAngle;
      }
    }

    return new Arc(center, radius, startAngle, endAngle, { ...this.attributes });
  }

  getBoundingBox() {
    // Sample arc for bounding box
    const points = this.getDisplayPoints(32);
    const xs = points.map(p => p.x);
    const ys = points.map(p => p.y);
    const zs = points.map(p => p.z);

    return {
      min: { x: Math.min(...xs), y: Math.min(...ys), z: Math.min(...zs) },
      max: { x: Math.max(...xs), y: Math.max(...ys), z: Math.max(...zs) },
    };
  }

  toJSON() {
    return {
      ...super.toJSON(),
      center: this.center.toObject(),
      radius: this.radius,
      startAngle: this.startAngle,
      endAngle: this.endAngle,
      plane: this.plane,
      pointIds: this._pointIds || null,
      centerPointId: this._centerPointId || null,
    };
  }

  static fromJSON(json) {
    const arc = new Arc(
      Vector.fromObject(json.center),
      json.radius,
      json.startAngle,
      json.endAngle,
      {
        ...json.attributes,
        pointIds: json.pointIds || null,
        centerPointId: json.centerPointId || null,
      }
    );
    arc.id = json.id;
    arc.plane = json.plane;
    arc.createdAt = json.createdAt;
    arc.modifiedAt = json.modifiedAt;
    return arc;
  }
}

export default Arc;
